import React, { useMemo } from "react";
import { Popover } from "ui-components/popover/popover";
import Amount from "ui-components/molecules/amount/amount";
import { CustomCellTypes } from "ui-components/table/custom-cells/customCell";
import { PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import "../outboundPlanningList.scss";

export interface PlanningFreightTotalsMetric {
  id: string;
  label: string;
  tooltip?: string;
  type: CustomCellTypes;
  count?: boolean;
  sumField?: keyof PlanningFreightProbill;
}

export const PLANNING_FREIGHT_TOTALS_METRICS: PlanningFreightTotalsMetric[] = [
  {
    id: "probillCount",
    label: "Probill",
    tooltip: "Count of all probills in the list",
    type: CustomCellTypes.number,
    count: true,
  },
  {
    id: "probillWeight",
    label: "Weight",
    tooltip: "Sum of probill weight",
    type: CustomCellTypes.number,
    sumField: "probillWeight",
  },
  {
    id: "probillPieces",
    label: "Pieces",
    tooltip: "Sum of probill pieces",
    type: CustomCellTypes.number,
    sumField: "probillPieces",
  },
  {
    id: "probillFreightCharge",
    label: "Freight Charge",
    tooltip: "Sum of probill freight charge",
    type: CustomCellTypes.amount,
    sumField: "probillFreightCharge",
  },
];

interface PlanningFreightListTotalsBarProps {
  rows: PlanningFreightProbill[];
  rowHeight?: number;
  metrics?: PlanningFreightTotalsMetric[];
}

function formatMetricValue(type: CustomCellTypes, value: unknown): React.ReactNode {
  if (value == null || value === "") return "—";
  if (type === CustomCellTypes.amount) {
    return <Amount amount={Number(value)} />;
  }
  return Number(value).toLocaleString("en", { useGrouping: true, maximumFractionDigits: 2 });
}

function resolveMetricValue(metric: PlanningFreightTotalsMetric, rows: PlanningFreightProbill[]): unknown {
  if (metric.count) return rows.length;
  if (metric.sumField) {
    return rows.reduce(
      (sum, row) => sum + (Number(row[metric.sumField!]) || 0),
      0
    );
  }
  return null;
}

export const PLANNING_PICKUP_TOTALS_METRICS: PlanningFreightTotalsMetric[] = [
  {
    id: "pickupCount",
    label: "Pickups",
    tooltip: "Count of all pickups in the list",
    type: CustomCellTypes.number,
    count: true,
  },
  {
    id: "probillWeight",
    label: "Weight",
    tooltip: "Sum of pickup weight",
    type: CustomCellTypes.number,
    sumField: "probillWeight",
  },
  {
    id: "probillPieces",
    label: "Pieces",
    tooltip: "Sum of pickup pieces",
    type: CustomCellTypes.number,
    sumField: "probillPieces",
  },
];

const PlanningFreightListTotalsBar: React.FC<PlanningFreightListTotalsBarProps> = ({
  rows,
  rowHeight = 44,
  metrics = PLANNING_FREIGHT_TOTALS_METRICS,
}) => {
  const resolvedMetrics = useMemo(
    () =>
      metrics.map((metric) => ({
        ...metric,
        value: resolveMetricValue(metric, rows),
      })),
    [metrics, rows]
  );

  return (
    <div
      className="planning-freight-list-totals-bar"
      role="row"
      style={{ minHeight: rowHeight }}
    >
      <div className="planning-freight-list-totals-bar__label">Totals</div>
      <div className="planning-freight-list-totals-bar__metrics">
        {resolvedMetrics.map((metric) => (
          <div className="planning-freight-list-totals-bar__metric" key={metric.id}>
            <span className="planning-freight-list-totals-bar__metric-label">{metric.label}</span>
            {metric.tooltip ? (
              <Popover
                trigger="hover"
                values={
                  <div className="planning-freight-list-totals-bar__tooltip">{metric.tooltip}</div>
                }
              />
            ) : null}
            <span className="planning-freight-list-totals-bar__metric-value">
              {formatMetricValue(metric.type, metric.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanningFreightListTotalsBar;
